<template>
  <v-card color="card" class="elevation-2 rounded-lg">
    <v-sheet color="primary">
      <v-card-title class="pa-2">
        <div class="text-h6 ml-2 white--text">
          {{ getLabel("workspaces_permissions") }}
        </div>
      </v-card-title>
    </v-sheet>
    <v-card-text>
      <v-row>
        <v-col cols="5">
          <v-card>
            <v-card-text>
              <v-checkbox
                :input-value="readableWorkspaces.includes('*')"
                :label="getLabel('permission_read_all')"
                :readonly="!editMode"
                class="text-black"
                @change="permissionToReadAll"
              />
              <v-checkbox
                :input-value="writableWorkspaces.includes('*')"
                :label="getLabel('permission_write_all')"
                :readonly="!editMode"
                class="text-black"
                @change="permissionToWriteAll"
              />
              <v-select
                class="mb-8"
                :items="workspacesWithPermissions"
                item-text="name"
                item-value="code"
                :value="defaultWorkspaceComputed"
                :label="getLabel('default')"
                :readonly="!editMode"
                hide-details
                :clearable="editMode"
                :no-data-text="getLabel('table_no_data')"
                @change="changeDefaultWorksapce"
              />

              <div v-if="editMode">
                <v-row>
                  <v-col cols="6">
                    <v-btn color="primary" block large @click="cancelEdit">
                      <v-icon>mdi-close-outline</v-icon>
                      {{ getLabel('cancel') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn color="success" block large @click="saveEdit">
                      <v-icon>mdi-content-save</v-icon> {{ getLabel('save') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
              <div v-else>
                <v-btn color="primary" block large @click="startEdit()">
                  <v-icon>mdi-pencil</v-icon> {{ getLabel("edit_permissions") }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="7">
          <v-card>
            <v-card-text>
              <v-row>
                <v-col cols="8">
                  <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Recherche" outlined />
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="projectSearch"
                    :items="projectsToShow"
                    item-text="label"
                    item-value="key"
                    outlined
                    :label="getLabel('project')"
                  />
                </v-col>
              </v-row>
              <v-data-table
                :headers="workspacesHeaders"
                :items="allWorkspacesSortedFiltered"
                :search="search"
                :items-per-page="-1"
                hide-default-footer
                fixed-header
                disable-sort
                :no-data-text="getLabel('table_no_data')"
                height="300"
              >
                <template #[`item.project`]="{ item }">
                  {{ getProjectLabel(item.project) }}
                </template>
                <template #[`item.read`]="{ item }">
                  <div v-if="!editMode">
                    <v-icon
                      v-if="readableWorkspacesReact.includes(item.code) || readableWorkspacesReact.includes('*')"
                      color="success"
                    >
                      mdi-check-bold
                    </v-icon>
                    <v-icon v-else color="error">mdi-close-thick</v-icon>
                  </div>
                  <v-simple-checkbox
                    v-else
                    color="primary"
                    :ripple="false"
                    :value="readableWorkspaces.includes(item.code) || readableWorkspaces.includes('*')"
                    class="mr-0"
                    @input="(value) => checkedReadWorkspace(value, item.code)"
                  />
                </template>
                <template #[`item.write`]="{ item }">
                  <div v-if="!editMode">
                    <v-icon
                      v-if="writableWorkspacesReact.includes(item.code) || writableWorkspacesReact.includes('*')"
                      color="success"
                    >
                      mdi-check-bold
                    </v-icon>
                    <v-icon v-else color="error">mdi-close-thick</v-icon>
                  </div>
                  <v-simple-checkbox
                    v-else
                    color="primary"
                    :ripple="false"
                    :value="writableWorkspaces.includes(item.code) || writableWorkspaces.includes('*')"
                    class="mr-0"
                    @input="(value) => checkedWriteWorkspace(value, item.code)"
                  />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';
import { useRoute } from 'vue2-helpers/vue-router';

import { getGraphqlSchema } from '@/graphql/index';
import { useMainStore } from '@/store/index';
import { getLabel, EventBus } from '@/utils/index';

export default {
  setup () {
    const store = useMainStore();

    const route = useRoute();

    const object = route.name === 'manageProfile' ? 'collaborator' : 'group';

    /* Projects Data */

    const projects = store.workspacesProjects;

    const { roles } = store;

    const editMode = ref(false);

    const objectRoles = computed(() => store[object].roles);

    const readableWorkspaces = ref([...store[object].readWorkspaces]);
    const writableWorkspaces = ref([...store[object].writeWorkspaces]);

    const sortWorkspaces = () => {
      const possibleWorkspaces = store.workspaces.filter(w => {
        // CRM Recruiter role must be able to have permissions to read and write on collaborator workspace. For more details ask Rosty
        if (w.code === 'collaborator' && objectRoles.value.includes('recruiter')) {
          return w;
        }

        if (objectRoles.value.includes(w.project) || objectRoles.value.includes('admin')) {
          return w;
        }
      });
      const sortedWorkspaces = possibleWorkspaces.slice().sort((a, b) => {
        if (writableWorkspaces.value.includes(a.code)) {
          return -1;
        }

        if (readableWorkspaces.value.includes(a.code) && !readableWorkspaces.value.includes(b.code)) {
          return -1;
        }

        return 0;
      });

      return sortedWorkspaces;
    };

    const projectsToShow = computed(() => {
      let projectsList = [];

      if (objectRoles.value.includes('admin')) {
        projectsList = [...projects];
      } else if (projects) {
        const workspaces = sortWorkspaces();
        projects.forEach(p => {
          if (workspaces.find(w => w.project === p.key)) {
            projectsList.push(p);
          }
        });
      }

      projectsList.unshift({ key: null, label: getLabel('all') });

      return projectsList;
    });

    const defaultWorkspace = ref(store[object].defaultWorkspace);

    const readableWorkspacesReact = computed(() => store[object].readWorkspaces);
    const writableWorkspacesReact = computed(() => store[object].writeWorkspaces);

    const defaultWorkspaceComputed = computed(() => ref(store[object].defaultWorkspace).value);

    const changeDefaultWorksapce = newVal => {
      defaultWorkspace.value = newVal;
    };

    const search = ref('');

    const projectSearch = ref(projectsToShow.value[0].key);

    const workspacesBeforeEditing = ref([]);

    const workspacesFiltered = workspaces => {
      // Filter out test workspaces in both cases
      const filteredWorkspaces = projectSearch.value === null
        ? workspaces.filter(w => !w.code?.startsWith('test_'))
        : workspaces.filter(w => (w.project === projectSearch.value) && !w.code?.startsWith('test_'));

      return filteredWorkspaces;
    };

    const workspacesHeaders = computed(() => {
      const baseHeaders = [
        {
          text: 'Name',
          value: 'name',
          align: 'left'
        }
      ];

      if (projectSearch.value === null) {
        baseHeaders.push({
          text: 'Project',
          value: 'project',
          align: 'left'
        });
      }

      baseHeaders.push(
        { text: 'Read', value: 'read', align: 'center' },
        { text: 'Write', value: 'write', align: 'center' }
      );

      return baseHeaders;
    });

    const allWorkspacesSortedFiltered = computed(() => {
      if (editMode.value) {
        return workspacesFiltered(workspacesBeforeEditing.value);
      }

      return workspacesFiltered(sortWorkspaces());
    });

    const startEdit = () => {
      workspacesBeforeEditing.value = sortWorkspaces();
      readableWorkspaces.value = [...store[object].readWorkspaces];
      writableWorkspaces.value = [...store[object].writeWorkspaces];
      defaultWorkspace.value = store[object].defaultWorkspace;
      editMode.value = true;
    };

    const cancelEdit = () => {
      editMode.value = false;

      readableWorkspaces.value = [...store[object].readWorkspaces];
      writableWorkspaces.value = [...store[object].writeWorkspaces];
      defaultWorkspace.value = store[object].defaultWorkspace;
    };

    // Update collaborator
    const { mutate: mutateUpdateCollaborator, onDone: doneUpdateCollaborator, onError: errorUpdateCollaborator } = useMutation(getGraphqlSchema('updateCollaboratorWorkspaces'), () => ({
      variables: {
        uuid: store.collaborator.uuid,
        readWorkspaces: readableWorkspaces.value,
        writeWorkspaces: writableWorkspaces.value,
        defaultWorkspace: defaultWorkspace.value
      }
    }));

    doneUpdateCollaborator(result => {
      store.loading = false;

      // when the creatorCollaborator mutation finishes
      EventBus.$emit('show-notifications', [{
        message: getLabel('permission_saved'),
        type: 'success'
      }]);

      // change only the attributes that have been saved
      store.collaborator = { ...store.collaborator, ...result.data.updateCollaborator };
    });

    errorUpdateCollaborator(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('permission_not_saved'),
        type: 'success'
      }]);
    });
    /** ******************************************** */

    // Update group
    const { mutate: mutateUpdateGroup, onDone: doneUpdateGroup, onError: errorUpdateGroup } = useMutation(getGraphqlSchema('updateGroupWorkspaces'), () => ({
      variables: {
        uuid: store.group.uuid,
        readWorkspaces: readableWorkspaces.value,
        writeWorkspaces: writableWorkspaces.value,
        defaultWorkspace: defaultWorkspace.value
      }
    }));

    doneUpdateGroup(result => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('permission_saved'),
        type: 'success'
      }]);

      // change only the attributes that have been saved
      store.group = { ...store.group, ...result.data.updateGroup };
    });

    errorUpdateGroup(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('permission_not_saved'),
        type: 'success'
      }]);
    });
    /** ******************************************** */

    const saveEdit = () => {
      editMode.value = false;
      store.loading = true;

      if (object === 'collaborator') {
        mutateUpdateCollaborator();
      } else {
        mutateUpdateGroup();
      }
    };

    const permissionToReadAll = active => {
      if (active) {
        readableWorkspaces.value = ['*'];
      } else {
        // Remove permission to write all if selected
        if (writableWorkspaces.value.includes('*')) {
          writableWorkspaces.value = [];
        }

        readableWorkspaces.value = [...writableWorkspaces.value];
      }
    };

    const permissionToWriteAll = active => {
      if (active) {
        writableWorkspaces.value = ['*'];
        readableWorkspaces.value = ['*'];
      } else {
        writableWorkspaces.value = [];
      }
    };

    const workspacesFromActivesProjects = computed(() => {
      let workspaces = [];
      projects.forEach(p => {
        if (store[object].roles.includes(p.key) || store[object].roles.includes('admin')) {
          workspaces = workspaces.concat(store.workspaces.filter(w => w.project === p.key));
        }
      });
      return workspaces;
    });

    function checkedReadWorkspace (active, code) {
      if (active) {
        readableWorkspaces.value.push(code);

        // Check if all the checkboxes are selected
        if (readableWorkspaces.value.length === store.workspaces.length) {
          readableWorkspaces.value = ['*'];
        }
      } else {
        // Check if all workspaces are selected as readable
        if (readableWorkspaces.value.includes('*')) {
          readableWorkspaces.value = workspacesFromActivesProjects.value.map(w => w.code);
        }
        // Remove write pewrmission if selected
        if (writableWorkspaces.value.includes(code)) {
          writableWorkspaces.value = writableWorkspaces.value.filter(workspace => workspace !== code);
        }

        if (writableWorkspaces.value.includes('*')) {
          writableWorkspaces.value = workspacesFromActivesProjects.value.map(w => w.code).filter(workspace => workspace !== code);
        }

        // Remove from readableWorkspaces
        readableWorkspaces.value = readableWorkspaces.value.filter(workspace => workspace !== code);
      }
    }

    function checkedWriteWorkspace (active, code) {
      if (active) {
        writableWorkspaces.value.push(code);
        if (!readableWorkspaces.value.includes('*')) {
          if (!readableWorkspaces.value.includes(code)) {
            readableWorkspaces.value.push(code);
          }
          // if all reads are selected
          if (readableWorkspaces.value.length === store.workspaces.length) {
            readableWorkspaces.value = ['*'];
          }
        }

        // Check if all the checkboxes are selected
        if (writableWorkspaces.value.length === store.workspaces.length) {
          writableWorkspaces.value = ['*'];
          readableWorkspaces.value = ['*'];
        }
      } else {
        // Check if all workspaces are selected as writable
        if (writableWorkspaces.value.includes('*')) {
          // add all workspaces
          writableWorkspaces.value = workspacesFromActivesProjects.value.map(w => w.code);
        }

        // Remove from just the one from writableWorkspaces
        writableWorkspaces.value = writableWorkspaces.value.filter(workspace => workspace !== code);
      }
    }

    const workspacesWithPermissions = computed(() => {
      const selectedWorkspaces = readableWorkspaces.value;

      if (selectedWorkspaces.includes('*')) {
        let workspaces = [];
        projects.forEach(p => {
          if (store[object].roles.includes(p.key) || store[object].roles.includes('admin')) {
            workspaces = workspaces.concat(store.workspaces.filter(w => w.project === p.key));
          }
        });
        return workspaces;
      }

      return store.workspaces.filter(w => selectedWorkspaces.includes(w.code));
    });

    const getProjectLabel = projectKey => {
      const project = projects.find(p => p.key === projectKey);
      return project ? project.label : '';
    };

    watch(workspacesWithPermissions, possibleDefaultWorkspaces => {
      // Change defaultWorkspace if read permission to the selected defaultWorkspaces was removed
      if (!possibleDefaultWorkspaces.find(w => w.code === defaultWorkspace.value)) {
        defaultWorkspace.value = null;
      }
    });

    onMounted(() => {
      EventBus.$on('cancel-workspaces-edit', cancelEdit);
    });

    return {
      getLabel,
      projects,
      roles,
      workspacesHeaders,
      projectsToShow,
      readableWorkspaces,
      writableWorkspaces,
      readableWorkspacesReact,
      writableWorkspacesReact,
      defaultWorkspace,
      defaultWorkspaceComputed,
      changeDefaultWorksapce,
      search,
      projectSearch,
      allWorkspacesSortedFiltered,
      editMode,
      startEdit,
      cancelEdit,
      saveEdit,
      permissionToReadAll,
      permissionToWriteAll,
      checkedReadWorkspace,
      checkedWriteWorkspace,
      workspacesWithPermissions,
      getProjectLabel
    };
  }
};
</script>
